import type { IconName } from '@mdvip/shared/components/blocks/Icon';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Alert, AlertDescription, AlertTitle } from '@mdvip/shared/components/ui/alert';
import { Badge } from '@mdvip/shared/components/ui/badge';
import { Button } from '@mdvip/shared/components/ui/button';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@mdvip/shared/components/ui/card';
import { Separator } from '@mdvip/shared/components/ui/separator';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { useFormatDate } from '@mdvip/shared/hooks/useFormatDate';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { Can } from '@mdvip/shared/system/AbilityProvider';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { MemberProfile, ProfilePostalAddress } from '@/hooks/profile/profileApi';

interface ProfileDetailsCardProps {
  member: MemberProfile;
  onEdit: () => void;
}

// Per-section grid overrides — shared between the real card and its skeleton so loading and
// loaded states can't drift into different column counts. See `ProfileSectionProps.gridClassName`.
const CONTACT_GRID = 'grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2';
const INSURANCE_EMPLOYMENT_GRID = 'grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-4';

interface ProfileSectionProps {
  title: string;
  icon: IconName;
  children: ReactNode;
  /** Overrides the default 2-col/16px-gap grid — Contact and Insurance & Employment each use
   * wider gaps in the handoff, and the latter goes to 4 columns at a wide enough width. */
  gridClassName?: string;
}

/** Section heading carries a leading glyph, matching the design's per-section icon (user/phone/map-pin/etc). */
function ProfileSection({ title, icon, children, gridClassName }: ProfileSectionProps) {
  return (
    <div data-slot="profile-section">
      <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground">
        <Icon name={icon} size="sm" className="text-primary" />
        {title}
      </h2>
      <div className={gridClassName ?? 'grid grid-cols-1 gap-4 sm:grid-cols-2'}>{children}</div>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string | null;
  /** Renders the value as a status Badge instead of plain text — mirrors the design's Status pill. */
  pill?: boolean;
}

/** A single read-only label/value pair. Falls back to `notOnFile` copy for the sparse-record case. */
function Field({ label, value, pill }: FieldProps) {
  const { t } = useTranslation('members');
  return (
    <div data-slot="profile-field">
      <div className="text-xs text-muted-foreground">{label}</div>
      {pill && value ? (
        <div className="mt-1">
          <Badge variant="success">{value}</Badge>
        </div>
      ) : (
        <div className="mt-0.5 text-sm font-medium text-foreground">{value ?? t('profile.notOnFile')}</div>
      )}
    </div>
  );
}

interface AddressFieldProps {
  label: string;
  address: ProfilePostalAddress | null;
}

/**
 * Tinted container matching the design's address card treatment — a leading house glyph, the
 * street on its own line, city/state/zip directly beneath it.
 */
function AddressField({ label, address }: AddressFieldProps) {
  const { t } = useTranslation('members');
  const cityLine = [address?.city, address?.state].filter(Boolean).join(', ');
  const hasAddress = Boolean(address?.line1 || cityLine);

  return (
    <div className="min-w-0 rounded-xl bg-tint-brand-soft p-4" data-slot="profile-address-field">
      <div className="mb-2 text-xs font-semibold text-muted-foreground">{label}</div>
      {hasAddress ? (
        <address className="grid grid-cols-[auto_1fr] items-start gap-x-2 gap-y-1 text-sm font-medium not-italic text-foreground">
          <Icon name="house" size="sm" className="mt-0.5 shrink-0 text-muted-foreground" />
          <span className="min-w-0 break-words">
            {address?.line1}
            {address?.line2 ? `, ${address.line2}` : ''}
          </span>
          <div className="col-start-2 min-w-0 break-words">
            {cityLine}
            {address?.zip ? ` ${address.zip}` : ''}
          </div>
        </address>
      ) : (
        <div className="text-sm font-medium text-muted-foreground">{t('profile.notOnFile')}</div>
      )}
    </div>
  );
}

/**
 * The Profile Details card — member's legal name (static, no edit affordance) plus four read-only
 * sections sourced from `myMemberInfo`: Member Information, Contact, Address, Insurance &
 * Employment. The Edit button opens the sibling Edit flow; it never touches the name itself, which
 * is locked and only changeable through Member Services.
 */
export function ProfileDetailsCard({ member, onEdit }: ProfileDetailsCardProps) {
  const { t } = useTranslation('members');
  const { formatDateOnly } = useFormatDate();

  const genderLabel = member.gender ? t(`profile.genderValues.${member.gender}`) : null;
  const contactMethodLabel = member.preferredContactMethod
    ? t(`profile.contactMethodValues.${member.preferredContactMethod}`)
    : null;

  return (
    // `gap-4` overrides Card's own `gap-6` between header and content — the handoff's title
    // row sits `mb-4` (16px) above its sections, not Card's default 24px.
    <Card className="gap-4" data-slot="card-profile-details">
      <CardHeader>
        <CardTitle className="text-base">{t('profile.detailsTitle')}</CardTitle>
        <CardAction>
          <Can I="update" a="Member">
            <Button size="sm" onClick={onEdit} className="min-h-11 gap-2">
              <Icon name="pencil" size="sm" /> {t('profile.edit')}
            </Button>
          </Can>
        </CardAction>
      </CardHeader>
      {/* `space-y-8` (not -6): the handoff's per-section `pb-8` + its own `gap-8` between section
          boxes total ~64px border-to-border — closer to this than the smaller default. */}
      <CardContent className="space-y-8">
        <ProfileSection title={t('profile.sections.memberInformation')} icon="user">
          <Field
            label={t('profile.fields.dateOfBirth')}
            value={member.dateOfBirth ? formatDateOnly(member.dateOfBirth) : null}
          />
          <Field label={t('profile.fields.gender')} value={genderLabel} />
          <Field
            label={t('profile.fields.memberSince')}
            value={member.memberSince ? formatDateOnly(member.memberSince) : null}
          />
          <Field label={t('profile.fields.memberStatus')} value={member.memberStatus} pill />
          <Field label={t('profile.fields.mdvipId')} value={member.mdvipId} />
        </ProfileSection>

        <Separator />

        {/* Phone group, then Email group — matches the handoff's field order: Primary + Cell
            phone, then Email + Preferred contact method. */}
        <ProfileSection title={t('profile.sections.contact')} icon="phone" gridClassName={CONTACT_GRID}>
          <Field label={t('profile.fields.phone')} value={member.phone} />
          <Field label={t('profile.fields.mobilePhone')} value={member.mobilePhone} />
          <Field label={t('profile.fields.email')} value={member.email} />
          <Field label={t('profile.fields.preferredContactMethod')} value={contactMethodLabel} />
        </ProfileSection>

        <Separator />

        <ProfileSection title={t('profile.sections.address')} icon="mapPin">
          <AddressField label={t('profile.fields.mailingAddress')} address={member.mailingAddress} />
          <AddressField label={t('profile.fields.otherAddress')} address={member.otherAddress} />
        </ProfileSection>

        <Separator />

        <ProfileSection
          title={t('profile.sections.insuranceEmployment')}
          icon="shieldCheck"
          gridClassName={INSURANCE_EMPLOYMENT_GRID}
        >
          <Field label={t('profile.fields.primaryInsurance')} value={member.primaryInsurance} />
          <Field label={t('profile.fields.secondaryInsurance')} value={member.secondaryInsurance} />
          <Field label={t('profile.fields.employer')} value={member.employer} />
          <Field label={t('profile.fields.jobTitle')} value={member.jobTitle} />
        </ProfileSection>
      </CardContent>
    </Card>
  );
}

/** One field's placeholder — label bar + value bar, sized to roughly match real content. */
function FieldSkeleton() {
  return (
    <div>
      <Skeleton className="h-3 w-20" />
      <Skeleton className="mt-1.5 h-4 w-36" />
    </div>
  );
}

/** One address block's placeholder — matches `AddressField`'s tinted container shape. */
function AddressFieldSkeleton() {
  return (
    <div className="min-w-0 rounded-xl bg-tint-brand-soft p-4">
      <Skeleton className="h-3 w-28" />
      <Skeleton className="mt-4 h-4 w-full" />
      <Skeleton className="mt-2 h-4 w-2/3" />
    </div>
  );
}

/**
 * Loading placeholder matching the handoff's skeleton state exactly — section headings and icons
 * render as real text immediately; only the field values beneath them are placeholder bars.
 */
export function ProfileDetailsCardSkeleton() {
  const { t } = useTranslation('members');
  return (
    <Card className="gap-4" data-slot="card-profile-details-skeleton" aria-busy="true">
      <CardHeader>
        <CardTitle className="text-base">{t('profile.detailsTitle')}</CardTitle>
        {/* No Edit button placeholder — the handoff only renders it once loading is false. */}
      </CardHeader>
      <CardContent className="space-y-8">
        <ProfileSection title={t('profile.sections.memberInformation')} icon="user">
          <FieldSkeleton />
          <FieldSkeleton />
          <FieldSkeleton />
          <FieldSkeleton />
          <FieldSkeleton />
        </ProfileSection>

        <Separator />

        <ProfileSection title={t('profile.sections.contact')} icon="phone" gridClassName={CONTACT_GRID}>
          <FieldSkeleton />
          <FieldSkeleton />
          <FieldSkeleton />
          <FieldSkeleton />
        </ProfileSection>

        <Separator />

        <ProfileSection title={t('profile.sections.address')} icon="mapPin">
          <AddressFieldSkeleton />
          <AddressFieldSkeleton />
        </ProfileSection>

        <Separator />

        <ProfileSection
          title={t('profile.sections.insuranceEmployment')}
          icon="shieldCheck"
          gridClassName={INSURANCE_EMPLOYMENT_GRID}
        >
          <FieldSkeleton />
          <FieldSkeleton />
          <FieldSkeleton />
          <FieldSkeleton />
        </ProfileSection>
      </CardContent>
    </Card>
  );
}

interface ProfileDetailsCardErrorProps {
  error: unknown;
}

/** Error state — no retry button, since `useMyProfile` doesn't expose one (see its own comment). */
export function ProfileDetailsCardError({ error }: ProfileDetailsCardErrorProps) {
  const { t } = useTranslation('members');
  const tError = useErrorTranslation();
  return (
    <Alert variant="destructive" data-slot="card-profile-details-error">
      <Icon name="alertTriangle" weight="solid" />
      <AlertTitle>{t('profile.loadErrorTitle')}</AlertTitle>
      <AlertDescription>{tError(extractGqlErrorCode(error))}</AlertDescription>
    </Alert>
  );
}
